import styled from 'styled-components';
import React, {FC} from 'react';

import {mediaBreakpointDown, mediaBreakpointOnly, mediaBreakpointUp} from '../../../../lib/styleVariables';
import {paletteR4, fontSizesMixinsR4, fontSizesMixinsMobileR4} from '../../../../lib/styleVariablesR4';
import {ImageTextBlock, ImageTextBlockProps} from '../../../molecules/textImageBlock';
import {StylableProps} from '../../../../lib/styleHelpers';
import {hiddenMobileDownStyle, hiddenTabletUpStyle} from '../../../../lib/responsiveClassNames';
import {Slider} from '../../../common/slider/basic';

type directionType = 'row' | 'column';

export interface DynamicImageTextXSGridR4Props {
  accentColor?: string;
  direction?: directionType;
  withSliderOnMobile?: boolean;
}

interface ImageTextXSGridR4Props extends DynamicImageTextXSGridR4Props, StylableProps {
  title: string;
  items: ImageTextBlockProps[];
}

const StyledGridWrapper = styled.div<{direction?: directionType}>`
  display: flex;
  flex-direction: ${props => props.direction};
  justify-content: space-between;
  align-items: ${props => (props.direction === 'column' ? 'center' : 'flex-start')};
  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
  }
`;

const StyledTitle = styled.div<{titleColor?: string; isColumn: boolean}>`
  ${props => (props.isColumn ? fontSizesMixinsR4.h2() : fontSizesMixinsR4.h3())}
  color: ${props => props.titleColor || 'inherit'};
  width: ${props => (props.isColumn ? '100%' : '240px')};
  min-width: ${props => (props.isColumn ? '100%' : '240px')};
  margin: ${props => (props.isColumn ? '0 0 85px' : '0 60px 0 0')};
  text-align: ${props => (props.isColumn ? 'center' : 'left')};

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h3()}
    text-align:center;
    width: 100%;
    margin: 0 0 30px;
  }
`;

const StyledGridList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledGridItem = styled.div<{direction?: directionType; withSliderOnMobile?: boolean}>`
  display: flex !important;
  justify-content: center;
  padding-left: 15px;
  padding-right: 15px;
  ${mediaBreakpointUp('desktop')} {
    width: ${props => (props.direction === 'row' ? '33.3333%' : '25%')};

    &:not(:nth-child(-n + ${props => (props.direction === 'row' ? '3' : '4')})) {
      margin-top: 55px;
    }
  }

  ${mediaBreakpointOnly('tablet')} {
    width: 50%;

    &:not(:nth-child(-n + 2)) {
      margin-top: 38px;
    }
  }

  ${mediaBreakpointDown('mobile')} {
    width: 100%;

    &:not(:first-child) {
      margin-top: 38px;
    }

    ${props => props.withSliderOnMobile && 'margin: 0 0 40px'};
  }

  a {
    color: ${paletteR4.black.base};
  }
`;

const StyledDesktop = styled.div`
  width: 100%;
  ${hiddenMobileDownStyle}
`;

const StyledMobile = styled.div`
  width: 100%;
  ${hiddenTabletUpStyle}
`;

const sliderSettings = {
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 500,
  arrows: false,
  dots: true
};

export const ImageTextXSGridR4: FC<ImageTextXSGridR4Props> = props => {
  const {title, items, accentColor, className, direction = 'row', withSliderOnMobile} = props;

  const elements = items.map((item: any) => (
    <StyledGridItem key={item.title} direction={direction} withSliderOnMobile={withSliderOnMobile}>
      <ImageTextBlock
        className={className}
        type="grid"
        {...item}
        iconColor={accentColor}
        direction={direction}
      />
    </StyledGridItem>
  ));

  const renderItems = () => {
    const staticItems = <StyledGridList>{elements}</StyledGridList>;

    if (withSliderOnMobile)
      return (
        <>
          <StyledDesktop>{staticItems}</StyledDesktop>
          <StyledMobile>
            <Slider items={elements} settings={sliderSettings} />
          </StyledMobile>
        </>
      );

    return staticItems;
  };

  return (
    <StyledGridWrapper direction={direction}>
      <StyledTitle titleColor={accentColor} isColumn={direction === 'column'}>
        {title}
      </StyledTitle>
      {renderItems()}
    </StyledGridWrapper>
  );
};
